import QRCode from 'qrcode';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../components/icons';
import { useT } from '../i18n';
import type { LabelSpec } from './Equipment';

// Common A4 label sheets. Height is per label; columns across the page.
const SIZES = {
  small: { cols: 4, h: '25mm', key: 'labels.sizeSmall' },
  medium: { cols: 3, h: '38mm', key: 'labels.sizeMedium' },
  large: { cols: 2, h: '50mm', key: 'labels.sizeLarge' },
} as const;
type SizeKey = keyof typeof SIZES;

function readLabels(state: unknown): LabelSpec[] {
  const fromState = (state as { labels?: LabelSpec[] } | null)?.labels;
  if (fromState?.length) return fromState;
  try {
    return JSON.parse(sessionStorage.getItem('tcdesk.labels') ?? '[]');
  } catch {
    return [];
  }
}

/** Printable sheet of QR labels. Opens with the labels chosen on the equipment or kits pages. */
export function LabelsPage() {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const labels = useMemo(() => readLabels(location.state), [location.state]);
  const [size, setSize] = useState<SizeKey>('medium');
  const [copies, setCopies] = useState(1);
  const [images, setImages] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out: Record<string, string> = {};
      for (const l of labels) {
        out[l.code] = await QRCode.toDataURL(l.code, { errorCorrectionLevel: 'M', margin: 1, width: 240 });
      }
      if (!cancelled) setImages(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [labels]);

  const s = SIZES[size];
  const all = labels.flatMap((l) => Array.from({ length: copies }, () => l));
  const ready = Object.keys(images).length === labels.length;

  return (
    <div className="main" style={{ maxWidth: 960, margin: '0 auto' }}>
      <div className="no-print stack" style={{ marginBottom: 16 }}>
        <div className="spread">
          <div>
            <button className="back btn-ghost" style={{ border: 0, background: 'none', cursor: 'pointer', padding: 0 }} onClick={() => navigate(-1)}>
              ← {t('common.back')}
            </button>
            <h1>{t('labels.title', { n: all.length })}</h1>
          </div>
          <button className="btn btn-primary" onClick={() => window.print()} disabled={!ready || !all.length}>
            <Icon.print />
            {t('labels.print')}
          </button>
        </div>
        <div className="row">
          <label className="label" htmlFor="l-size">{t('labels.size')}</label>
          <select id="l-size" className="select" style={{ width: 'auto' }} value={size} onChange={(e) => setSize(e.target.value as SizeKey)}>
            {(Object.keys(SIZES) as SizeKey[]).map((k) => (
              <option key={k} value={k}>{t(SIZES[k].key)}</option>
            ))}
          </select>
          <label className="label" htmlFor="l-copies">{t('labels.copies')}</label>
          <input id="l-copies" className="input" style={{ width: 80 }} type="number" min={1} max={10} value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(10, Number(e.target.value) || 1)))} />
        </div>
        <p className="muted small" style={{ margin: 0 }}>{t('labels.hint')}</p>
      </div>
      {labels.length === 0 ? (
        <div className="empty">{t('labels.none')}</div>
      ) : (
        <div className="labels" style={{ ['--cols' as string]: s.cols, ['--h' as string]: s.h }}>
          {all.map((l, i) => (
            <div className="label-cell" key={`${l.code}-${i}`}>
              {images[l.code] && <img src={images[l.code]} alt={l.code} />}
              <div className="t">
                <div className="code">{l.code}</div>
                <div className="name">{l.name}</div>
                <div className="co">Translation Centre</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
